import JsonURL from '@jsonurl/jsonurl';
import { paramsFromOrder } from 'configurations/OrderEntity/paramsOrders';
import { getOrdersRequest } from 'configurations/OrderEntity/requestOrders';
import { paramsFromShipment } from 'configurations/ShipmentEntity/paramsShipments';
import { getShipmentsRequest } from 'configurations/ShipmentEntity/requestShipments';
import { createContext, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { PANEL_FILTERS } from 'utils/constants/Filters';
import {
  IFilterContext,
  TFilter,
  TFilterValue,
} from 'utils/context/FilterContext/types';
import {
  getFilterCount,
  hasFilters,
  isEmpty,
} from 'utils/context/FilterContext/utils';

const FilterContext = createContext<IFilterContext>({
  cache: {},
  clearFilters: () => null,
  filters: {},
  getFilterCount: () => 0,
  getOrdersRequest: () => ({}),
  getShipmentsRequest: () => ({}),
  hasFilters: () => false,
  paramsFromOrder: () => ({}),
  paramsFromShipment: () => ({}),
  revertFiltersKey: () => null,
  setFilters: () => null,
  setFiltersKey: () => null,
  setFiltersKeys: () => null,
});

interface IFilterContextProvider {
  children: React.ReactNode;
}

function FilterContextProvider({ children }: IFilterContextProvider) {
  const history = useHistory();
  const [cache, setCache] = useState<TFilter>({});
  const [didMount, setDidMount] = useState(false);
  const [filters, setFilters] = useState<TFilter>(() => {
    const param = history.location.search.replace(/\+/g, '%2B');
    const urlFilters = new URLSearchParams(param).get('filters');
    let initial = {
      listView: 'all-shipments',
      viewType: 'shipment',
    };
    try {
      if (urlFilters) {
        initial = JsonURL.parse(urlFilters, { AQF: true, emptyValue: [] });
      }
    } catch {}
    return initial;
  });

  useEffect(() => {
    if (!didMount) {
      setDidMount(true);
    } else {
      const query = new URLSearchParams(history.location.search);
      try {
        const next = JsonURL.stringify(filters, { AQF: true });
        query.set('filters', next || '');
      } catch {
        query.delete('filters');
      }
      history.replace({
        search: query
          .toString()
          .replace(/%21/g, '!')
          .replace(/%28/g, '(')
          .replace(/%29/g, ')')
          .replace(/%2B/g, '+')
          .replace(/%2C/g, ',')
          .replace(/%3A/g, ':'),
      });
    }
  }, [didMount, filters, history]);

  const { clearFilters, setFiltersKey, setFiltersKeys } = useMemo(
    () => ({
      clearFilters: (matchFilters = PANEL_FILTERS) => {
        setFilters((prev) => {
          const next = { ...prev };
          matchFilters.forEach((match) => delete next[match]);
          return next;
        });
      },
      setFiltersKey: (key: string, value: TFilterValue) => {
        setFilters((prev) => {
          setCache(prev);
          const next = { ...prev, [key]: value };
          if (!value.length) delete next[key];
          return next;
        });
      },
      setFiltersKeys: (keys: { [key: string]: TFilterValue }) => {
        setFilters((prev) => {
          setCache(prev);
          const next = { ...prev, ...keys };
          Object.keys(keys).forEach((key) => {
            if (isEmpty(next[key])) delete next[key];
          });
          return next;
        });
      },
    }),
    [],
  );

  const value = useMemo(() => {
    const revertFiltersKey = (key: string) => {
      if (cache[key]) {
        setFilters((prev) => ({
          ...prev,
          [key]: cache[key],
        }));
      }
    };

    return {
      cache,
      clearFilters,
      filters,
      getFilterCount: () => getFilterCount(filters),
      getOrdersRequest: () => getOrdersRequest(filters),
      getShipmentsRequest: () => getShipmentsRequest(filters),
      hasFilters: (matchFilters = PANEL_FILTERS) =>
        hasFilters(filters, matchFilters),
      paramsFromOrder,
      paramsFromShipment,
      revertFiltersKey,
      setFilters,
      setFiltersKey,
      setFiltersKeys,
    };
  }, [filters, cache, clearFilters, setFiltersKey, setFiltersKeys]);

  return (
    <FilterContext.Provider value={value}>{children}</FilterContext.Provider>
  );
}

export { FilterContext, FilterContextProvider };
